import { useMemo, useState } from 'react';
import { Typography } from '@mui/material';

import Number from 'src/components/common/Number';
import Tooltip from 'src/components/common/Tooltip';
import GradientButton from 'src/components/GradientButton';
import IconButton from 'src/components/IconButton';
import { VOTE_OPTION } from 'src/constants/proposals';
import { DYDX_TOKEN } from 'src/constants/tokens';
import type { Proposals } from 'src/types/proposals';
import { gt } from 'src/utils/number';
import { isMnemonic, isMnemonicContain } from 'src/utils/string';

import {
  Container,
  ContentsContainer,
  DescriptionContainer,
  Divider,
  Header,
  HeaderTitle,
  InputContainer,
  ProposalTitleContainer,
  StyledBottomSheet,
  StyledTextarea,
  TxInfoContainer,
  TxInfoItemContainer,
  TxInfoItemLeftContainer,
  TxInfoItemRightAmountContainer,
} from './styled';

import { ReactComponent as CloseIcon } from 'src/assets/svg/Close.svg';

type SignConfirmBottomSheetProps = Omit<React.ComponentProps<typeof StyledBottomSheet>, 'children'> & {
  title: string;
  currentProposal?: Proposals;
  voteOption: string;
  descriptionText: string;
  displayEstimatedGasFeeAmount: string;
  isButtonDisabled: boolean;
  isEstimation?: boolean;
  currentMemo?: string;
  setCurrentMemo: (memo: string) => void;
  onClickConfirm?: () => void;
};

export default function SignConfirmBottomSheet({
  descriptionText,
  title,
  currentProposal,
  voteOption,
  displayEstimatedGasFeeAmount,
  isButtonDisabled,
  isEstimation = false,
  currentMemo,
  setCurrentMemo,
  onClose,
  onClickConfirm,
  ...remainder
}: SignConfirmBottomSheetProps) {
  const [inputText, setInputText] = useState('');

  const gasFee = useMemo(() => String(parseFloat(displayEstimatedGasFeeAmount)), [displayEstimatedGasFeeAmount]);

  const voteOptionText = useMemo(() => {
    if (voteOption === VOTE_OPTION.VOTE_OPTION_YES) return 'YES';
    if (voteOption === VOTE_OPTION.VOTE_OPTION_NO) return 'NO';
    if (voteOption === VOTE_OPTION.VOTE_OPTION_NO_WITH_VETO) return 'VETO';
    if (voteOption === VOTE_OPTION.VOTE_OPTION_ABSTAIN) return 'ABSTAIN';

    return '';
  }, [voteOption]);

  const errorMessage = useMemo(() => {
    if (isMnemonicContain(inputText)) {
      return 'Memo contains mnemonic';
    }

    if (isEstimation) {
      return 'Please wait for the transaction to be estimated.';
    }
    return '';
  }, [inputText, isEstimation]);

  return (
    <StyledBottomSheet {...remainder} onClose={onClose}>
      <Container>
        <ContentsContainer>
          <Header>
            <HeaderTitle>
              <Typography variant="h2">{title}</Typography>
            </HeaderTitle>
            <IconButton
              onClick={() => {
                onClose?.({}, 'backdropClick');
              }}
            >
              <CloseIcon />
            </IconButton>
          </Header>
          <DescriptionContainer>
            <Typography variant="h15">{descriptionText}</Typography>
          </DescriptionContainer>

          <TxInfoContainer>
            <TxInfoItemContainer>
              <TxInfoItemLeftContainer>
                <Typography variant="h7">Proposal Title</Typography>

                <ProposalTitleContainer>
                  <Typography variant="h6">{`#${currentProposal?.id || ''}. ${currentProposal?.title || '-'}`}</Typography>
                </ProposalTitleContainer>
              </TxInfoItemLeftContainer>
            </TxInfoItemContainer>

            <TxInfoItemContainer>
              <TxInfoItemLeftContainer>
                <Typography variant="h7">My Vote</Typography>
              </TxInfoItemLeftContainer>

              <Typography variant="h6">{voteOptionText}</Typography>
            </TxInfoItemContainer>

            <Divider />

            <TxInfoItemContainer>
              <TxInfoItemLeftContainer>
                <Typography variant="h7">Estimated Gas Fee</Typography>
              </TxInfoItemLeftContainer>
              <TxInfoItemRightAmountContainer>
                <Number typoOfIntegers="h4" typoOfDecimals="h7">
                  {gt(gasFee, '0') ? gasFee : '0'}
                </Number>
                &nbsp;
                <Typography variant="h6">{DYDX_TOKEN.symbol}</Typography>
              </TxInfoItemRightAmountContainer>
            </TxInfoItemContainer>
          </TxInfoContainer>

          <InputContainer>
            <Typography variant="h7">Memo (Optional)</Typography>

            <StyledTextarea
              multiline
              minRows={2}
              maxRows={2}
              placeholder="Do NOT input your personal data such as secret phrase."
              onChange={(e) => {
                if (isMnemonic(e.currentTarget.value) || e.currentTarget.value.length > 256 || e.currentTarget.value.match(/^0x/)) {
                  return;
                }

                setInputText(e.currentTarget.value);

                setCurrentMemo(e.currentTarget.value);
              }}
              value={currentMemo}
            />
          </InputContainer>
        </ContentsContainer>
        <Tooltip varient="error" title={errorMessage} placement="top" arrow>
          <div>
            <GradientButton
              disabled={!!errorMessage || isButtonDisabled}
              onClick={() => {
                onClickConfirm?.();
              }}
            >
              <Typography variant="h4">CONFIRM</Typography>
            </GradientButton>
          </div>
        </Tooltip>
      </Container>
    </StyledBottomSheet>
  );
}
